import { create, useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import Divider from '@shared/components/Divider/Divider';
import Popup from '@shared/components/Popup/Popup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ModalFooter from 'components/ModalFooter';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { Schemas } from 'utils/yup/schemas/validationSchemas';

import ChatNameField from './ChatNameField';
import { ChatPrivacyEnum } from './ChatPrivacyField/ChatPrivacyEnum';
import ChatPrivacyField from './ChatPrivacyField/ChatPrivacyField';
import useCreateMessengerRoom from './useCreateMessengerRoom';
import InviteMembersField from '../InviteMembersField/InviteMembersField';
import useChatInviteField from '../InviteMembersField/useChatInviteField';

interface NewChatInviteFieldProps {
  isPublic: boolean;
}

const NewChatInviteField = ({ isPublic }: NewChatInviteFieldProps) => {
  const { invitees, errors, addInvitee, removeInvitee, setInvitees } = useChatInviteField();
  return (
    <InviteMembersField
      isPublic={isPublic}
      invitees={invitees}
      errors={errors}
      addInvitee={addInvitee}
      removeInvitee={removeInvitee}
      setInvitees={setInvitees}
    />
  );
};

export type NewChatFormType = {
  chatName: string;
  chatPrivacy: ChatPrivacyEnum;
  invitees: string[];
};

const CreateNewChatModal = create(() => {
  const { t } = useTranslation();
  const { remove } = useModal(CreateNewChatModal);
  const { workspaceId } = useWorkspacesContext();
  const form = useForm<NewChatFormType>({
    mode: 'all',
    resolver: yupResolver(Schemas.getNewChatSchema()),
    defaultValues: {
      chatName: '',
      chatPrivacy: workspaceId ? ChatPrivacyEnum.PRIVATE : ChatPrivacyEnum.PUBLIC,
      invitees: [],
    },
  });
  const { handleSubmit } = form;
  const { createMessengerRoom, isLoading } = useCreateMessengerRoom();

  const submitForm = handleSubmit(async (data) => {
    const success = await createMessengerRoom(data);
    if (success) remove();
  });

  const isPublic = form.watch('chatPrivacy') === ChatPrivacyEnum.PUBLIC;
  const isSuccessDisabled = !form.formState.isValid;
  const isInWorkspace = !!workspaceId;

  return (
    <FormProvider {...form}>
      <Popup isVisible>
        <Popup.FullScreenContainer className="md:w-[573px]">
          <Popup.Header className="flex-col !mx-0 px-4 pt-4 !shadow-none !border-none !justify-start">
            <div className="flex w-full justify-between items-center">
              <span className="text-size-md font-medium">{t('messenger.new_chat_title')}</span>
              <Popup.CloseIcon closePopup={remove} className="!static" />
            </div>
          </Popup.Header>
          <div className="px-4">
            <Divider />
          </div>
          <div className="flex flex-col grow overflow-y-auto gap-4 p-4">
            <ChatNameField />
            {isInWorkspace && <ChatPrivacyField />}
            <Divider />
            <NewChatInviteField isPublic={isPublic} />
          </div>
          <div className="px-4">
            <Divider />
          </div>
          <ModalFooter
            onCloseClick={remove}
            onSubmit={submitForm}
            successButtonText={t('create')}
            cancelText={t('cancel')}
            successDisabled={isSuccessDisabled}
            isLoading={isLoading}
            hasBorder={false}
          />
        </Popup.FullScreenContainer>
      </Popup>
    </FormProvider>
  );
});

export default CreateNewChatModal;
